import { useMutation } from 'react-query';
import api from '@tyrio/api-factory';
import { DBRitamApi } from '@tyrio/dto';
import { ToastHelper, ToastType } from '@tyrio/ui-library';

export const useAddToRitam = () => {
  const { mutate, isLoading } = useMutation(
    async (ean: string) => {
      return await api.fetch<DBRitamApi['create_article']>(
        'ritam_create_article',
        { ean }
      );
    },
    {
      onSuccess: (data, ean) => {
        // Show success toast with EAN
        const successMessage =
          typeof data === 'string' ? data : 'Successfully added to Ritam';
        ToastHelper.showToast(
          '',
          ToastType.SUCCESS,
          undefined,
          `Added EAN ${ean}: ${successMessage}`
        );
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError: (error: any, ean) => {
        // Extract the error message from various possible locations in the response
        let errorMessage = 'Error occurred';

        // Try to get the detailed error message
        if (error?.response?.data) {
          if (typeof error.response.data === 'string') {
            // If the response data is a direct string
            errorMessage = error.response.data;
          } else if (error.response.data.error?.name) {
            // If there's a nested error object with a name property (the actual error message)
            errorMessage = error.response.data.error.name;
          } else if (error.response.data.message) {
            // If there's a message property
            errorMessage = error.response.data.message;
          } else {
            // Try to extract most relevant part without the stack trace
            try {
              const data = error.response.data;
              if (data.error && typeof data.error === 'object') {
                const errorValues = Object.values(data.error);
                errorMessage =
                  errorValues.length > 0 && typeof errorValues[0] === 'string'
                    ? errorValues[0]
                    : JSON.stringify(data);
              } else {
                errorMessage = JSON.stringify(data);
              }
            } catch (e) {
              console.error('Error extracting error message:', e);
            }
          }
        } else if (error?.message) {
          // Use standard error message property
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          // If error is already a string
          errorMessage = error;
        }

        ToastHelper.showToast(
          '',
          ToastType.ERROR,
          undefined,
          `Failed to add EAN ${ean}: ${errorMessage}`
        );
      },
    }
  );

  /**
   * Add multiple items to Ritam one by one
   */
  const addItemsToRitam = async (eans: string[]) => {
    if (!eans.length) return;

    // Show initial info toast
    ToastHelper.showToast(
      '',
      ToastType.INFO,
      undefined,
      `Sending ${eans.length} item${eans.length > 1 ? 's' : ''} to Ritam...`
    );

    let successCount = 0;
    let errorCount = 0;
    const failedItems: string[] = [];
    const successItems: string[] = [];

    // Process each EAN sequentially
    for (const ean of eans) {
      try {
        const response = await api.fetch<DBRitamApi['create_article']>(
          'ritam_create_article',
          { ean }
        );

        successCount++;
        successItems.push(ean);

        // Show individual success toast with EAN and response message
        const successMessage =
          typeof response === 'string'
            ? response
            : 'Successfully added to Ritam';
        ToastHelper.showToast(
          '',
          ToastType.SUCCESS,
          undefined,
          `Added EAN ${ean}: ${successMessage}`
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        errorCount++;
        failedItems.push(ean);

        // Extract the error message from various possible locations in the response
        let errorMessage = 'Error occurred';

        // Try to get the detailed error message
        if (error?.response?.data) {
          if (typeof error.response.data === 'string') {
            // If the response data is a direct string
            errorMessage = error.response.data;
          } else if (error.response.data.error?.name) {
            // If there's a nested error object with a name property (the actual error message)
            errorMessage = error.response.data.error.name;
          } else if (error.response.data.message) {
            // If there's a message property
            errorMessage = error.response.data.message;
          } else {
            // Try to extract most relevant part without the stack trace
            try {
              const data = error.response.data;
              if (data.error && typeof data.error === 'object') {
                const errorValues = Object.values(data.error);
                errorMessage =
                  errorValues.length > 0 && typeof errorValues[0] === 'string'
                    ? errorValues[0]
                    : JSON.stringify(data);
              } else {
                errorMessage = JSON.stringify(data);
              }
            } catch (e) {
              console.error('Error extracting error message:', e);
            }
          }
        } else if (error?.message) {
          // Use standard error message property
          errorMessage = error.message;
        } else if (typeof error === 'string') {
          // If error is already a string
          errorMessage = error;
        }

        console.error(`Error adding ${ean} to Ritam:`, error);

        // Show individual error toast with EAN and specific error message
        ToastHelper.showToast(
          '',
          ToastType.ERROR,
          undefined,
          `Failed to add EAN ${ean}: ${errorMessage}`
        );
      }
    }

    // Show final result toast with list of items
    let resultMessage = `Complete! Sent ${successCount} of ${eans.length} items to Ritam.`;

    if (successCount > 0) {
      resultMessage += `\nSuccessful: ${successItems.join(', ')}`;
    }

    if (errorCount > 0) {
      resultMessage += `\nFailed: ${failedItems.join(', ')}`;
      ToastHelper.showToast('', ToastType.WARNING, undefined, resultMessage);
    } else {
      ToastHelper.showToast('', ToastType.SUCCESS, undefined, resultMessage);
    }
  };

  return {
    addItemToRitam: mutate,
    addItemsToRitam,
    isLoading,
  };
};
